/**
 * RaceCard — tarjeta visual de una race_target (v15.5-§8.3.C+D+E).
 *
 * §8.3.C: solo render (sin handlers).
 * §8.3.D: handlers onEdit/onDelete → botones condicionales.
 * §8.3.E: handler onPromote → botón "Marcar como principal" solo cuando
 *         la race NO es primary (la primary ya muestra el badge — no
 *         tiene sentido ofrecer la acción ahí).
 *
 * Layout mobile-first con patrón Card del proyecto:
 * `rounded-md border border-border-subtle bg-carbon-raised p-4`.
 *
 * Reutiliza labels existentes:
 * - Distance: helper compartido `distanceLabel` (v15.5-§8.3.E extracción).
 * - Surface:  t.profile.surfaceAsfalto/Trail/Mixto (local — solo
 *             RaceCard lo usa por ahora).
 *
 * Fecha formateada con toLocaleDateString("es-ES", {day: "numeric",
 * month: "short", year: "numeric"}) — patrón ya usado en
 * SessionDetailPage. Forzamos T00:00:00 para evitar el bug TZ
 * cazado en S11.2 (parseo UTC midnight → día anterior en TZs
 * negativas).
 */
import { distanceLabel } from "@/components/races/distanceLabel";
import { t } from "@/i18n/es";
import type { RaceTarget } from "@/types/api";

interface Props {
  race: RaceTarget;
  /** §8.3.D: callback al pulsar editar. Undefined → no botón. */
  onEdit?: (id: number) => void;
  /** §8.3.D: callback al pulsar borrar. Undefined → no botón. */
  onDelete?: (id: number) => void;
  /** §8.3.E: callback al pulsar "Marcar como principal". Solo se
   *  renderiza si !is_primary (la primary no necesita la acción). */
  onPromote?: (id: number) => void;
  /** v16.3 B.4: callback al pulsar "Añadir resultado". Solo se renderiza
   *  si !race.completed && race.race_date <= today (carrera ya pasada
   *  sin resultado registrado). */
  onAddResult?: (id: number) => void;
}

function _todayIsoUtc(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getUTCDate()).padStart(2, "0")}`;
}

/** Mapeo de surface backend → clave i18n en t.profile.*. */
const SURFACE_LABEL_KEY: Record<string, keyof typeof t.profile> = {
  "asfalto": "surfaceAsfalto",
  "trail": "surfaceTrail",
  "mixto": "surfaceMixto",
};

function _formatRaceDate(iso: string): string {
  // T00:00:00 fuerza interpretación local (bug TZ S11.2).
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function _surfaceLabel(surface: string): string {
  const key = SURFACE_LABEL_KEY[surface];
  return key ? t.profile[key] : surface;
}

export function RaceCard({
  race,
  onEdit,
  onDelete,
  onPromote,
  onAddResult,
}: Props) {
  const dateLabel = _formatRaceDate(race.race_date);
  const distanceLabelText = distanceLabel(race.distance);
  const surfaceLabelText = _surfaceLabel(race.surface);

  // v16.3 B.4: gate del botón "Añadir resultado". Solo si race ya pasada
  // (race_date <= today) y atleta no registró resultado todavía. El padre
  // (RacesPage) pasa onAddResult que abre RaceResultModal.
  const today = _todayIsoUtc();
  const canAddResult = Boolean(
    onAddResult && !race.completed && race.race_date <= today,
  );

  // §8.3.D: si llega cualquier handler, mostramos la fila de acciones.
  // Cada botón se renderiza condicionalmente. En §8.3.C sin handlers →
  // sin botones (tests de §8.3.C siguen verdes; cambio aditivo).
  const hasAnyAction = Boolean(
    onEdit || onDelete || onPromote || canAddResult,
  );

  return (
    <article
      data-testid={`race-card-${race.id}`}
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-carbon-raised p-4"
    >
      {/* Fila superior: nombre (si existe) + badge Principal. */}
      <div className="flex items-start justify-between gap-3">
        {race.name ? (
          <h3 className="text-sm font-medium text-bone">{race.name}</h3>
        ) : (
          // Sin name: no renderizamos h3 (el bloque queda con solo la
          // línea de datos como cuerpo). flex-1 absorbería el espacio.
          <span className="sr-only">{distanceLabelText}</span>
        )}
        {race.is_primary && (
          <span
            data-testid="race-card-primary-badge"
            className="shrink-0 rounded-full border border-lime/50 bg-lime/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-lime"
          >
            {t.races.primaryBadge}
          </span>
        )}
      </div>

      {/* Línea de datos: fecha · distancia · superficie. */}
      <p className="text-xs text-bone-muted">
        {dateLabel} · {distanceLabelText} · {surfaceLabelText}
      </p>

      {/* Fila de acciones (solo si llega algún handler). v15.5-§8.3.D:
          onEdit + onDelete. v15.5-§8.3.E: onPromote (botón discreto
          solo cuando is_primary=false). */}
      {hasAnyAction && (
        <div className="mt-1 flex flex-wrap items-center gap-4">
          {canAddResult && onAddResult && (
            <button
              type="button"
              data-testid={`race-card-add-result-button-${race.id}`}
              onClick={() => onAddResult(race.id)}
              className="text-xs text-lime underline-offset-4 hover:underline"
            >
              {t.races.cardAddResultAction}
            </button>
          )}
          {onPromote && !race.is_primary && (
            <button
              type="button"
              data-testid={`race-card-promote-button-${race.id}`}
              onClick={() => onPromote(race.id)}
              className="text-xs text-lime underline-offset-4 hover:underline"
            >
              {t.races.cardPromoteAction}
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              data-testid={`race-card-edit-button-${race.id}`}
              onClick={() => onEdit(race.id)}
              className="text-xs text-bone-muted underline-offset-4 hover:text-bone hover:underline"
            >
              {t.races.cardEditAction}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              data-testid={`race-card-delete-button-${race.id}`}
              onClick={() => onDelete(race.id)}
              className="text-xs text-bone-muted underline-offset-4 hover:text-danger hover:underline"
            >
              {t.races.cardDeleteAction}
            </button>
          )}
        </div>
      )}
    </article>
  );
}
